import type { StudioPermission } from "@dnceclub/shared";
import { useAuth } from "@/lib/contexts/auth-context";
import { usePermission } from "@/lib/hooks/use-permission";
import { supabase } from "@/lib/supabase";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOwner?: boolean;
  requireEmailVerified?: boolean;
  skipOnboardingCheck?: boolean;
}

export function ProtectedRoute({
  children,
  requireOwner,
  requireEmailVerified = true,
  skipOnboardingCheck = false,
}: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [hasStudio, setHasStudio] = useState<boolean | null>(null);

  // Route protection logic

  // If we arrived from create-studio after successful creation, skip the DB check
  const studioJustCreated = location.state?.studioJustCreated === true;

  // Check owner onboarding status if needed
  // Only run for actual owners — staff members don't create studios
  useEffect(() => {
    if (!user || !requireOwner || skipOnboardingCheck || !profile?.is_owner) {
      return;
    }

    // Studio was just created — trust the navigation state instead of querying
    if (studioJustCreated) {
      setHasStudio(true);
      return;
    }

    const checkStudio = async () => {
      setCheckingOnboarding(true);

      try {
        const { data, error } = await supabase
          .from("studios")
          .select("id")
          .eq("owner_id", user.id)
          .maybeSingle();

        const hasStudioResult = !!data && !error;
        setHasStudio(hasStudioResult);
      } catch {
        // Don't redirect on transient failures — leave hasStudio as null
        // so the user sees the page instead of being bounced to create-studio
        console.error("[ProtectedRoute] Failed to check studio, skipping redirect");
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkStudio();
  }, [user, requireOwner, skipOnboardingCheck, profile?.is_owner, studioJustCreated]);

  // Show loading state
  if (loading || profile === undefined || checkingOnboarding) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-on-dark-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Email verification check
  if (requireEmailVerified && !user.email_confirmed_at) {
    return <Navigate to="/auth/email-verification-required" replace />;
  }

  // Owner portal access check — allow owners and staff with active roles
  if (requireOwner && profile) {
    const hasPortalAccess =
      profile.is_owner ||
      (profile.staffRoles && profile.staffRoles.length > 0);
    if (!hasPortalAccess) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4 text-on-light">
              Access Denied
            </h1>
            <p className="text-on-light-muted">
              You don't have permission to access this page.
            </p>
          </div>
        </div>
      );
    }
  }

  // Owner onboarding check — only actual owners need to create studios, not staff
  // Skip onboarding redirect if user has staff roles (they can access via staff access)
  const hasStaffRoles = profile?.staffRoles && profile.staffRoles.length > 0;
  if (requireOwner && profile?.is_owner && !skipOnboardingCheck && !hasStaffRoles) {
    // Redirect to create-studio if no studio
    if (hasStudio === false && location.pathname !== "/owner/create-studio") {
      return <Navigate to="/owner/create-studio" replace />;
    }

    // Redirect to dashboard if already onboarded and on create-studio page
    if (hasStudio === true && location.pathname === "/owner/create-studio") {
      return <Navigate to="/owner/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

// Convenience wrapper for owner routes
export function OwnerProtectedRoute({
  children,
  skipOnboarding = false,
}: {
  children: React.ReactNode;
  skipOnboarding?: boolean;
}) {
  return (
    <ProtectedRoute requireOwner={true} skipOnboardingCheck={skipOnboarding}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Route wrapper that checks for a specific studio permission.
 * Must be used inside a StudioContextProvider.
 * Owners bypass all permission checks. Staff need the specific permission granted.
 * Redirects to /owner/dashboard if the permission is not granted.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: StudioPermission;
  children: React.ReactNode;
}) {
  const { hasPermission } = usePermission();

  if (!hasPermission(permission)) {
    return <Navigate to="/owner/dashboard" replace />;
  }

  return <>{children}</>;
}
